import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useOrgId } from '@/lib/pulse/useEmployee';
import { myPulses, submitPulse, type PulseRow } from '@/lib/pulse/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { PulseButtonRow } from '@/components/employee/PulseButtonRow';
import { PulseSparkline } from '@/components/employee/PulseSparkline';
import { StreakBadge } from '@/components/employee/StreakBadge';
import { toast } from '@/hooks/use-toast';
import { Shield } from 'lucide-react';
import { ActiveSurveyCard } from '@/components/employee/ActiveSurveyCard';

function todayUTC() {
  return new Date().toISOString().slice(0, 10);
}

export default function EmployeeHome() {
  const orgId = useOrgId();
  const qc = useQueryClient();

  const { data: pulses = [], isLoading } = useQuery({
    queryKey: ['my_pulses', orgId],
    queryFn: async () => {
      const { data, error } = await myPulses(orgId!, 90);
      if (error) throw error;
      return (data ?? []) as PulseRow[];
    },
    enabled: !!orgId,
    staleTime: 0,
  });

  const today = pulses.find((p) => p.created_day === todayUTC());


  const submit = async (value: number) => {
    if (!orgId || today) return;
    const { error } = await submitPulse(orgId, value);
    if (error) {
      toast({ title: 'Could not submit', description: error.message, variant: 'destructive' });
      return;
    }
    toast({
      title: 'Pulse locked in',
      description: 'Thanks for checking in. You can record the next one tomorrow.',
    });
    qc.invalidateQueries({ queryKey: ['my_pulses', orgId] });
  };

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-8 space-y-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold">Today</h1>
          <p className="text-sm text-muted-foreground">One quick check-in.</p>
        </div>
        <StreakBadge pulses={pulses} />
      </header>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="font-heading text-lg">
              {today ? 'You\u2019ve checked in today' : 'How\u2019s work feeling today?'}
            </CardTitle>
            {today && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="secondary" className="cursor-help shrink-0">
                    Locked for today
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  Pulses lock once saved. You can record a new one tomorrow.
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          {today && (
            <CardDescription>
              You picked <span className="font-medium text-foreground">{today.value}</span>. Come back tomorrow for the next check-in.
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <PulseButtonRow
            value={today?.value ?? null}
            disabled={isLoading || !orgId || !!today}
            onSelect={submit}
          />
        </CardContent>
      </Card>

      {orgId && <ActiveSurveyCard orgId={orgId} />}

      {pulses.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-base">Your trend</CardTitle>
            <CardDescription>Only you can see this.</CardDescription>
          </CardHeader>
          <CardContent>
            <PulseSparkline pulses={pulses} days={14} />
          </CardContent>
        </Card>
      )}

      <Card className="bg-muted/40 border-dashed">
        <CardContent className="flex items-start gap-3 pt-6">
          <Shield className="h-5 w-5 text-primary mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium">Admins never see individual pulses.</p>
            <p className="text-sm text-muted-foreground mt-0.5">
              They only see averages when 5+ people have shared.
            </p>
            <Button asChild variant="link" className="px-0 h-auto mt-1">
              <Link to="/app/trust">Read the trust charter \u2192</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
